/**
 * The stage's avatar speaking without MuseTalk (docs/NEW-UI/09): its green-screen plate played at 25 fps, the recorded
 * mouths read from the reply's audio as the voice schedules it, keyed onto the stage. Until it is loaded — and for good
 * if it cannot be (no WebGL, no library on this machine) — the idle video shows.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { reportOnce } from '../core/diagnostics';
import type { ChromaKey } from '../stage/catalog';
import type { AvatarBridge } from '../voice/controller';
import { PLAYBACK_RATE } from '../voice/playback';
import { keyer } from './chroma-key';
import { FILL } from './KeyedVideo';
import { MouthLibrary } from './mouth-library';
import { MouthPlayer } from './mouth-player';
import { workerReader } from './mouth-reader';
import { PlateStore } from './plate';
import { SpeechTrack } from './speech-track';

// once per page: the face also stands in while MuseTalk connects, and comes back when it drops (~40 MB)
const loaded = new Map<string, Promise<{ plate: PlateStore; mouths: MouthLibrary }>>();

function load(plateId: string) {
  let pending = loaded.get(plateId);
  if (!pending) {
    pending = PlateStore.load(plateId).then(async (plate) => ({ plate, mouths: await MouthLibrary.load(plate) }));
    pending.catch(() => loaded.delete(plateId));
    loaded.set(plateId, pending);
  }
  return pending;
}

export function SpeakingIdle({ plateId, keying, bridge, fallback }: {
  plateId: string;
  keying: ChromaKey;
  bridge: AvatarBridge;
  fallback: ReactNode;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stop: (() => void) | null = null;
    let disposed = false;
    (async () => {
      const gl = canvasRef.current?.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false });
      if (!gl) throw new Error('no WebGL');
      const draw = keyer(gl, keying);
      const { plate, mouths } = await load(plateId);
      if (disposed) return;
      const reader = workerReader(mouths.model, PLAYBACK_RATE);
      void reader.read(new ArrayBuffer(PLAYBACK_RATE)); // warm the reader on half a second of silence: the first reply waits for it
      const track = new SpeechTrack(reader.read);
      const player = new MouthPlayer(document.createElement('canvas'), plate, mouths, (f) => draw(f, f.width, f.height));
      player.speak(track.speaking(() => bridge.clock()));
      // the reply's sound waits for its first mouths (the voice's gate): the mouth moves with the first syllable
      const unsubscribe = bridge.onSpeech((clause) => void track.add(clause).then(() => bridge.firstFrame(clause.turn)));
      player.start();
      stop = () => {
        unsubscribe();
        player.stop();
        reader.dispose();
      };
      setReady(true);
    })().catch((e: unknown) =>
      reportOnce('warn', 'avatar', `the idle face cannot speak (${plateId}): the idle video shows`, e));
    return () => {
      disposed = true;
      stop?.();
      setReady(false);
    };
  }, [plateId, keying, bridge]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} data-face="idle" data-speaking-idle={ready}>
      <canvas ref={canvasRef} style={{ ...FILL, position: 'absolute', inset: 0, opacity: ready ? 1 : 0 }} aria-hidden />
      {!ready && fallback}
    </div>
  );
}
